import { AlertTriangle, Send } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

interface EscalatePopoverProps {
  onEscalate: (shouldQualify: boolean, reason: string) => void;
  isRecommended?: boolean;
  disabled?: boolean;
}

export const EscalatePopover: React.FC<EscalatePopoverProps> = ({
  onEscalate,
  isRecommended = false,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [opinion, setOpinion] = useState<'yes' | 'no' | ''>('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!opinion) return;

    setIsSubmitting(true);
    try {
      await onEscalate(opinion === 'yes', reason);
      setOpen(false);
      setOpinion('');
      setReason('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setOpinion('');
      setReason('');
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'h-9 px-4 rounded-full text-xs font-bold transition-all flex items-center gap-1.5',
            // Disabled state: still visible but clearly inactive
            disabled && 'bg-zinc-800/60 text-zinc-600 cursor-not-allowed',
            // Active states
            !disabled && isRecommended &&
              'bg-amber-400 text-zinc-900 hover:bg-amber-300 active:scale-95',
            !disabled && !isRecommended &&
              'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300 active:scale-95',
          )}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          Escalate
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0 bg-zinc-900 border-zinc-800 rounded-2xl overflow-hidden"
        align="end"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-800">
          <h4 className="text-xs font-black text-white uppercase tracking-wider">
            Escalate to Slack
          </h4>
        </div>

        <div className="p-3 space-y-3">
          {/* Opinion toggle pills */}
          <div>
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider block mb-2">
              Should we qualify?
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOpinion('yes')}
                className={cn(
                  'flex-1 h-9 rounded-xl text-xs font-black uppercase tracking-wide transition-all active:scale-[0.98]',
                  opinion === 'yes'
                    ? 'bg-emerald-500 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300',
                )}
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setOpinion('no')}
                className={cn(
                  'flex-1 h-9 rounded-xl text-xs font-black uppercase tracking-wide transition-all active:scale-[0.98]',
                  opinion === 'no'
                    ? 'bg-zinc-200 text-zinc-900'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-300',
                )}
              >
                No
              </button>
            </div>
          </div>

          {/* Reason textarea */}
          <div>
            <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider block mb-2">
              Note <span className="font-normal text-zinc-600">(optional)</span>
            </span>
            <textarea
              placeholder="Add context..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-16 px-3 py-2 rounded-xl bg-zinc-800 text-white text-xs font-medium placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50 resize-none"
            />
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={isSubmitting}
              className="flex-1 h-9 rounded-xl bg-zinc-800 text-zinc-400 text-xs font-bold hover:bg-zinc-700 hover:text-zinc-300 transition-all active:scale-[0.98] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!opinion || isSubmitting}
              className={cn(
                'flex-1 h-9 rounded-xl text-xs font-bold transition-all active:scale-[0.98] flex items-center justify-center gap-1.5',
                opinion
                  ? 'bg-amber-400 text-zinc-900 hover:bg-amber-300'
                  : 'bg-zinc-800 text-zinc-600 cursor-not-allowed',
                isSubmitting && 'opacity-50',
              )}
            >
              <Send className="w-3 h-3" />
              {isSubmitting ? 'Sending' : 'Send'}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
